import React, { useState, useEffect } from 'react';

const STORAGE_KEY = 'kenro-comparedProducts';

const CompareProductContainer = () => {
  const [productData, setProductData] = useState([]);
  const [uniqueMetafields, setUniqueMetafields] = useState([]);

  const formatKey = (key) => key.replace(/^.*?\./, '').replace(/_/g, ' ');

  const getUniqueMetafields = (products) => {
    const set = new Set();
    products.forEach((p) => {
      Object.keys(p.product_metafields || {}).forEach((key) => set.add(key));
      Object.keys(p.variant_metafields || {}).forEach((key) => set.add(key));
    });
    return Array.from(set);
  };

  const updateProducts = (products) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(products));
    setProductData(products);
    setUniqueMetafields(getUniqueMetafields(products));
  };

  const removeProduct = (productId, variantId) => {
    const updated = productData.filter((p) => !(p.id === productId && p.variant_id === variantId));
    updateProducts(updated);
  };

  const fetchVariantData = async (product, variantId) => {
    try {
      const response = await fetch(`/products/${product.handle}?variant=${variantId}`);
      if (!response.ok) throw new Error('Network error');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const prodDataElement = doc.querySelector('#prodData');

      if (
        prodDataElement &&
        prodDataElement.textContent &&
        prodDataElement.textContent.trim().startsWith('{')
      ) {
        const newProductData = JSON.parse(prodDataElement.textContent);
        // Check if this variant is already in the list
        const exists = productData.some(
          (p) => p.id === newProductData.id && p.variant_id === newProductData.variant_id
        );
        if (!exists) {
          // Add as a new product entry
          updateProducts([...productData, { ...newProductData, variant_id: variantId }]);
        }
      } else {
        throw new Error('prodData not found or invalid');
      }
    } catch (err) {
      console.error('Failed to fetch variant data:', err);
    }
  };

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    console.log('Stored products:', stored);
    updateProducts(stored);
  }, []);

  // Sort products by type alphabetically
  const sortedProducts = [...productData].sort((a, b) => {
    if (!a.type) return 1;
    if (!b.type) return -1;
    return a.type.localeCompare(b.type);
  });

  if (productData.length === 0) {
    return (
      <div className="my-12 page-width">
        <p>No products to compare.</p>
      </div>
    );
  }

  return (
    <div className="my-12 page-width px-4">
      <ul className="flex gap-4 overflow-x-auto list-none compare-products snap-x snap-mandatory md:snap-none">
        {sortedProducts.map((product, index) => {
          console.log(product);
          return (
            <li
              key={product.id}
              className="min-w-[300px] md:min-w-[400px] snap-start p-4 compare-product border border-[#ececec] rounded-lg transition-all duration-200"
            >
              <div className="flex flex-col gap-4">
                <div className="relative w-full overflow-hidden">
                  <img
                    className="w-full object-cover sm:h-[240px] md:h-[300px] lg:h-[360px] xl:h-[420px]"
                    src={product.image}
                    alt={product.title}
                  />
                </div>
                <div>
                  <h3 className="mt-4 font-bold md:mt-0 text-black">{product.title}</h3>
                  <p className="mt-4 text-black" data-vat-excl-price={product.price_excl_vat}>
                    {(product.price / 100).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                  </p>
                </div>
                <div>
                  <label className="uppercase text-black font-medium">Options</label>
                  <select
                    value={product.variant_id}
                    onChange={(e) => fetchVariantData(product, e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                  >
                    <option value="" disabled>
                      Select an option
                    </option>
                    {product.variants.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-12 overflow-y-auto max-h-128 md:max-h-none">
                {product.metafields && (
                  <table className="w-full text-left border-collapse">
                    <tbody>
                      {Object.entries(product.metafields)
                        .filter(([_, value]) => value.variant !== null && typeof value.variant !== 'undefined' && value.variant !== 'N/A')
                        .map(([key, value], idx) => (
                          <tr key={key} className={idx % 2 === 0 ? 'bg-brand-blue/5' : 'bg-brand-blue/10'}>
                            <td className="p-2 font-light uppercase">{formatKey(key)}</td>
                            <td className="p-2">{value.variant}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="flex justify-between gap-4 mt-8">
                <a href={`/products/${product.handle}`} className="btn btn--secondary btn--sm">
                  View
                </a>
                <button
                  onClick={() => removeProduct(product.id, product.variant_id)}
                  className="btn btn--primary btn--sm"
                >
                  Remove
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default CompareProductContainer;
